import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, X } from 'lucide-react';
import api from '../../lib/api';
import RichEditor from '../../components/RichEditor';

const EMPTY = { title: '', slug: '', tag: 'Insights', excerpt: '', body: '', cover: '', published: true };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120);

export default function Articles() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/articles/admin');
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...edit, slug: edit.slug || slugify(edit.title) };
    try {
      if (edit.id) await api.put(`/articles/${edit.id}`, payload);
      else await api.post('/articles', payload);
      setEdit(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => { if (!window.confirm('Delete article?')) return; await api.delete(`/articles/${id}`); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-serif text-cream-50 text-3xl">Articles</h1>
          <p className="text-cream-100/60 text-sm mt-1">Publish news and insights with a full rich-text editor.</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-4 py-2 text-sm hover:bg-sage-600"><Plus className="h-4 w-4" /> New article</button>
      </div>

      <div className="bg-forest-900 border border-forest-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-forest-800 text-cream-100/50">
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Title</th>
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Tag</th>
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Status</th>
              <th className="p-3 font-normal uppercase tracking-widest text-[11px]">Date</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-cream-100/50">No articles yet.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="border-b border-forest-800/70">
                <td className="p-3"><div className="text-cream-50">{a.title}</div><div className="text-xs text-cream-100/50 font-mono">/insights/{a.slug}</div></td>
                <td className="p-3 text-cream-100/75">{a.tag}</td>
                <td className="p-3">{a.published ? <span className="text-sage-400 text-xs uppercase tracking-widest">Live</span> : <span className="text-cream-100/45 text-xs uppercase tracking-widest">Draft</span>}</td>
                <td className="p-3 text-cream-100/60">{new Date(a.created_at).toLocaleDateString('en-GB')}</td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEdit({ ...a })} className="text-sage-400 text-xs hover:underline">Edit</button>
                  <button onClick={() => remove(a.id)} className="text-red-400 text-xs hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <div className="bg-forest-900 border border-forest-800 w-full max-w-4xl max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b border-forest-800 sticky top-0 bg-forest-900">
              <h2 className="font-serif text-cream-50 text-xl">{edit.id ? 'Edit' : 'New'} article</h2>
              <button onClick={() => setEdit(null)} className="text-cream-100/60 hover:text-cream-50"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Title</label>
                <input value={edit.title||''} onChange={(e) => setEdit({ ...edit, title: e.target.value, slug: edit.id ? edit.slug : slugify(e.target.value) })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Slug</label>
                  <input value={edit.slug||''} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50 font-mono" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Tag</label>
                  <input value={edit.tag||''} onChange={(e) => setEdit({ ...edit, tag: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Cover image URL</label>
                <input value={edit.cover||''} onChange={(e) => setEdit({ ...edit, cover: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Excerpt</label>
                <textarea rows={2} value={edit.excerpt||''} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} className="w-full bg-forest-950 border border-forest-700 focus:border-sage-500 outline-none px-3 py-2 text-sm text-cream-50 resize-none" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-cream-100/60 mb-1.5">Body</label>
                <RichEditor value={edit.body || ''} onChange={(v) => setEdit({ ...edit, body: v })} placeholder="Write your article here..." />
              </div>
              <label className="flex items-center gap-3 text-cream-100/80">
                <input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-sage-500" /> Publish immediately
              </label>
            </div>
            <div className="p-5 border-t border-forest-800 flex justify-end gap-3 sticky bottom-0 bg-forest-900">
              <button onClick={() => setEdit(null)} className="px-4 py-2 border border-forest-700 text-cream-100/85 text-sm hover:border-sage-500">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 bg-sage-500 text-forest-900 px-5 py-2 text-sm hover:bg-sage-600"><Save className="h-4 w-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
